"use strict";

/**
 * This gets the full url with site path for content strings that don't include the site path.
 * @param {string} siteUrl
 * @param {string} url
 * @returns {string} full/usable url
 */
function getFullUrl(siteUrl, url) {
  if (url) {
    siteUrl = siteUrl || "";
    if(siteUrl.endsWith("/")) {
      siteUrl = siteUrl.slice(0, -1);
    }

    // likely a content path or relative url
    if (url.startsWith("/")) {
      return `${siteUrl}${url}`;
    }
    // fully-qualified url
    else if (url.match(/^.{3,5}:\/\//)) {
      return url;
    }

    // incomplete url, needs protocol
    return `https://${url}`;
  }
  return "";
}

/**
 * Appends ".html" to a string only if it doesn't start with "http" or is "#" or
 * already has a video or img extention
 * @param {String} url Any url
 */
function getUrlWithExtention(url) {
  const videoExt = ["mov", "avi", "wmv", "flv", "3gp", "mp4", "mpg", "webm"];
  const imageExt = ["jpg", "jpeg", "png", "gif", "tiff"];

  if (
    url &&
    url !== "#" &&
    !url.startsWith("http") &&
    !url.includes(".html") &&
    !url.startsWith("javascript") &&
    ![...videoExt, ...imageExt].some((ext) => url.endsWith(`.${ext}`))
  ) {
    return url + ".html";
  } else {
    return url;
  }
}

/**
 * Environment URLs
 * Note: These values were copied from ns-util runConfigService.js
 */
const environmentUrls = {
  local: "localhost",
  local4502: "localhost:4502",
  test: "test.nuskin.com",
  testStorefront: "test.mynuskin.com",
  testCloud: "tst.nuskin.io",
  dev: "dev.nuskin.com",
  devCloud: "dev.nuskin.io",
  prod: "www.nuskin.com",
  prodStorefront: ".mynuskin.com",
  prodCloud: "www.nuskin.io",
  prodPubServer: "prd-pubaws-aem",
  prodNgpilot: "ngpilot.nuskin.com",
  stage: "stage.nuskin.com",
  stageCloud: "stg.nuskin.io",
  feature: "dev.usw2.nuskin.io"
};

/**
 * Dev Environment URLs
 */
const devEnvironmentUrls = [environmentUrls.dev, environmentUrls.devCloud];

/**
 * Test Environment URLs
 */
const testEnvironmentUrls = [
  environmentUrls.test,
  environmentUrls.testStorefront,
  environmentUrls.feature,
  environmentUrls.testCloud,
  environmentUrls.local,
  environmentUrls.local4502
];

/**
 * Stage Environment URLs
 */
const stageEnvironmentUrls = [
  environmentUrls.stage,
  environmentUrls.stageCloud
];

/**
 * Prod Environment URLs
 */
const prodEnvironmentUrls = [
  environmentUrls.prod,
  environmentUrls.prodCloud,
  environmentUrls.prodStorefront,
  environmentUrls.prodNgpilot
];

/**
 * This attempts to get the environment based on the URL of nuskin.com known addresses.
 * @param {String} url
 */
function getEnvironmentFromUrl(url) {
  if (url) {
    // check if 'dev' environment
    if (devEnvironmentUrls.some((envUrl) => url.includes(envUrl))) {
      return "dev";
    }

    // check if 'test' environment
    if (testEnvironmentUrls.some((e) => url.includes(e))) {
      return "test";
    }

    // check if 'stage' environment
    if (stageEnvironmentUrls.some((envUrl) => url.includes(envUrl))) {
      return "stage";
    }

    // check if 'prod' environment
    if (prodEnvironmentUrls.some((envUrl) => url.includes(envUrl))) {
      return "prod";
    }
  }

  // environment is not known
  return "unknown";
}

module.exports = {
  getFullUrl,
  getUrlWithExtention,
  environmentUrls,
  devEnvironmentUrls,
  testEnvironmentUrls,
  stageEnvironmentUrls,
  prodEnvironmentUrls,
  getEnvironmentFromUrl
};
